<template>
  <div
    class="status-bar-helper-cores-floating"
    :class="license?.windows?.summary?.unlimitedHelperCores && size === 'small' ? 'cursor-default' : ''"
    :size="size"
    data-testid="status-bar-helper-cores-block"
  >
    <q-resize-observer @resize="value => (elSize = value)" />
    <div class="flex items-center">
      <div
        v-if="showSCLSection"
        class="mr-[2px]"
      >
        <i class="incredicon-solid-windows !text-[16px] text-Text/text_medium" />
        <i class="uil uil-linux !text-[16px] text-Text/text_medium" />
      </div>
      <div
        class="gordita-500-14-m status-bar-helper-cores-floating--title row"
        :style="`color: ${COLORS['Text/Dark']}`"
        data-testid="status-bar-helper-cores-title"
      >
        {{ titleText }}
        <HelperCoresTooltip
          v-if="license?.windows?.summary?.unlimitedHelperCores && size === 'large'"
          class="q-ml-xs text[16px]"
          v-model="labelTitleTooltipOpen"
          icon-size="16px"
        />
      </div>
    </div>

    <div class="col status-bar-helper-cores-floating--info-container">
      <div class="purchased--container">
        <div
          class="sans-700-22 purchased--value"
          data-testid="status-bar-helper-cores-purchased"
        >
          {{ titleValue }}
        </div>
      </div>
      <div
        v-if="size !== 'small' && !license?.windows?.summary?.unlimitedHelperCores"
        class="labeled--container"
      >
        <div
          class="gordita-400-11 labeled--title"
          data-testid="helper-cores-labeled"
        >
          <span data-testid="helper-cores-labeled-title">Helper Core Pool</span>
          <HelperCoresTooltip
            v-model="labelTitleTooltipOpen"
            icon-size="12px"
          />
        </div>
        <div
          class="sans-700-22 labeled--value"
          data-testid="status-bar-helper-cores-floating-total"
        >
          {{ license?.windows?.summary?.totalRegisteredFloatingCores?.toLocaleString() }}
        </div>
      </div>
    </div>
    <q-tooltip
      v-if="size === 'small' && !labelTitleTooltipOpen && !license?.windows?.summary?.unlimitedHelperCores"
      class="tooltip-background helper-cores-tooltip"
      :delay="200"
      style="padding: 8px 12px"
    >
      <div>
        <div class="gordita-500-11-m grid-container-element">
          <div
            data-testid="status-bar-helper-cores-pool-title"
            style="width: 154px"
          >
            Helper Core Pool
          </div>
          <div data-testid="status-bar-helper-cores-pool-total">{{ license?.windows?.summary?.totalRegisteredFloatingCores }}</div>
        </div>
      </div>
    </q-tooltip>
  </div>
</template>

<script>
import millify from 'millify';
import { defineComponent, inject } from 'vue';
import { useAgentsPageQuery } from '@/graphql/codegen/generated/graphql';
import { IKCoordinatorsService } from '@/modules/CoordinatorsService/CoordinatorService.InjectionKeys';
import COLORS from '@/design-system/colors.json';
import HelperCoresTooltip from '@/components/status-bar/components/tooltips/helper-cores-tooltip.vue';
import { Constants } from '@/const/globals';

export default defineComponent({
  components: {
    HelperCoresTooltip,
  },
  props: {
    size: {
      type: String,
      required: true,
    },
  },
  setup() {
    const coordinatorsService = inject(IKCoordinatorsService);

    const GQL_AgentsPageQuery = useAgentsPageQuery(() => ({ coordinatorId: coordinatorsService?.selectedCoordinator.value || '' }));
    return { GQL_AgentsPageQuery, COLORS };
  },
  data() {
    return {
      labelTitleTooltipOpen: false,
      elSize: { width: null, height: null },
    };
  },
  computed: {
    license() {
      return this.GQL_AgentsPageQuery?.result?.value?.ibManagerEntities?.license || {};
    },
    showSCLSection() {
      return this.license?.windows?.features?.productFeatures?.includes(Constants.LICENSE_FEATURES.SCL) && this.size !== 'small';
    },
    titleText() {
      return this.license?.windows?.summary?.unlimitedHelperCores ? 'Online Helper Cores' : 'Helper Cores';
    },
    titleValue() {
      if (this.license?.windows?.summary?.unlimitedHelperCores) {
        return this.license?.windows?.summary?.totalRegisteredFloatingCores?.toLocaleString();
      }
      return String(this.license?.windows?.summary?.totalFloatingCores).length >= 6
        ? millify(this.license?.windows?.summary?.totalFloatingCores)
        : this.license?.windows?.summary?.totalFloatingCores?.toLocaleString();
    },
  },
});
</script>

<style lang="scss" scoped>
.labeled--title {
  display: flex;
  align-items: center;
  gap: 3px;
}
.status-bar-helper-cores-floating--title {
  line-height: 16px;
}
.status-bar-helper-cores-floating {
  &[size='large'] {
    .status-bar-helper-cores-floating--info-container {
      display: flex;
      flex-direction: column;
    }
    .status-bar-helper-cores-floating--description {
      display: block;
    }
  }
  &[size='medium'] {
    .status-bar-helper-cores-floating--title {
      font-size: 13px !important;
      line-height: 20px;
      height: 20px;
    }
    .status-bar-helper-cores-floating--info-container {
      display: flex;
      flex-direction: column;
      .purchased--value {
        font-size: 20px !important;
        line-height: 27px;
        height: 27px;
      }
    }
    .status-bar-helper-cores-floating--description {
      display: block;
    }
  }
  &[size='small'] {
    pointer-events: initial;
    cursor: pointer;
    .status-bar-helper-cores-floating--title {
      text-align: center;
      line-height: 16px;
      font-size: 12px;
      height: 32px;
    }
    .status-bar-helper-cores-floating--info-container {
      display: flex;
      justify-content: center;
      align-items: center;
      .purchased--value {
        font-size: 16px;
        margin-bottom: 12px;
        line-height: 22px;
      }
    }
  }
}
.grid-container-element {
  display: grid;
  grid-template-columns: 0fr 0fr;
  grid-gap: 4px;
}
.status-bar-helper-cores-floating {
  flex-basis: 82px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 0 12px 12px 12px;
}
.status-bar-helper-cores-floating--info-container {
  color: $-text--dark;
  display: flex;
}

.status-bar-helper-cores-floating--info-container {
  flex-grow: 1;
}

.status-bar-helper-cores-floating {
  .progress {
    margin-top: auto;
  }
}
.labeled--container {
  margin-top: 18px;
}
.purchased--value {
  line-height: 36px;
}
.labeled--value {
  font-size: 20px;
  line-height: 27px;
}
</style>
